import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityFileTypeEnum,
  ActivityTypeEnum,
  OfferInterface,
} from '@sport-activities/sdk';
import { Subscription } from 'rxjs';

import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { ActivityCardVariantEnum } from '~/component/activity-card/enum/variant.enum';
import { VtmnButtonSizeEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-size.enum';
import { VtmnButtonVariantEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-variant.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { DividerVariantEnum } from '~/component/divider/enums/divider-variant.enum';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { hasPaymentProvider } from '~/core/helpers/organizer/payment-provider/payment-provider.helper';
import { Activity } from '~/core/model/activity.model';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { TitleService } from '~/core/services/title/title.service';
import { LocaleService } from '~/i18n/services/locale.service';
import { EditionLinkInterface } from '~/organizer/activity-edition/interfaces/edition-link.interface';
import { ActivitySelectionService } from '~/organizer/activity-planning/services/activity-selection.service';
import { DisplayPlansModeEnum } from '~/organizer/shared/components/shared/display-plans/display-plans.component';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';

@Component({
  selector: 'app-organizer-timeslot-pre-schedule',
  templateUrl: './pre-schedule.component.html',
})
export class OrganizerTimeslotPagePreScheduleComponent
  implements OnInit, OnDestroy
{
  private readonly subscriptions = new Subscription();

  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly dateLocale: Locale;
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly tipsVariant = TipsVariantEnum.WARNING;
  public readonly vtmnButtonSize = VtmnButtonSizeEnum;
  public readonly vtmnButtonVariant = VtmnButtonVariantEnum;

  public activity: Activity;
  public activityCardVariant = ActivityCardVariantEnum;
  public editionLinks: EditionLinkInterface[];
  public isTabletDevice: boolean;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly titleService: TitleService,
    private readonly localeService: LocaleService,
    private readonly activitySelectionService: ActivitySelectionService,
    protected readonly organizerSharedActivityService: OrganizerSharedActivityService,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly redirectionService: RedirectionService,
    private readonly matchMediaService: MatchMediaService,
    private readonly organizerSharedOrganizationService: OrganizerSharedOrganizationService,
    private readonly toggleService: ToggleService,
    private readonly translateService: TranslateService
  ) {
    this.titleService.setPageTitle(
      'seo.organizer.timeslot.pre_schedule.page.title'
    );
    this.dateLocale = this.localeService.dateLocale;
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.activatedRoute.data.subscribe(({ activityData }) => {
        this.activity = activityData.activity;
      })
    );

    this.subscriptions.add(
      this.matchMediaService.isTabletDevice().subscribe((isTabletDevice) => {
        this.isTabletDevice = isTabletDevice;
      })
    );
  }

  public get areOffersInvalid(): boolean {
    return !this.plans?.length;
  }

  public get backLink(): BackLinkInterface {
    return {
      path: this.localizeRouterService
        .translateRoute(`/organizer.activity.manage/${this.activity.sku}`)
        .toString(),
    };
  }

  public get displayPlansMode(): DisplayPlansModeEnum {
    return this.isBirthday
      ? DisplayPlansModeEnum.FULL
      : DisplayPlansModeEnum.LIGHT;
  }

  public get groupSize(): number {
    return (
      this.activitySelectionService.getOverloads(this.activity.sku)
        .maximumAttendeeCapacity ??
      this.activity?.indicativeMaximumAttendeeCapacity
    );
  }

  public get hasHeader(): boolean {
    return !this.isTabletDevice;
  }

  public get invalidPriceText(): string {
    return this.translateService.instant(
      'organizer.pre_schedule.invalid_activity_price',
      {
        onboardingUrl: `<a href="${this.localizeRouterService.translateRoute(
          '/organizer.onboarding.payment-1'
        )}" target="_blank" class="underline"></a>`,
      }
    );
  }

  public get isBirthday(): boolean {
    return (
      this.activity?.type === ('BIRTHDAY' as ActivityTypeEnum) ||
      this.activity?.type === ('PRODUCT_TEST' as ActivityTypeEnum)
    );
  }

  public get isDisabled(): boolean {
    return this.isPriceInvalid || this.areOffersInvalid || !this.groupSize;
  }

  public get isPriceInvalid(): boolean {
    if (
      this.toggleService.isEnabled(
        ToggleNameEnum.IgnorePriceValidationWithoutPaymentProvider
      )
    ) {
      return false;
    }

    return (
      !hasPaymentProvider(
        this.organizerSharedOrganizationService.organization
      ) && this.activity.maxPriceOffer?.price > 0
    );
  }

  public get picture(): string {
    return (
      this.activity.getMediaUrlByType(
        'MAIN_MEDIA' as ActivityFileTypeEnum,
        ActivityMediaResolutionEnum.ICON
      ) || this.activity.picture
    );
  }

  public get plans(): OfferInterface[] {
    return (
      this.activitySelectionService.getOverloads(this.activity.sku).offers ??
      this.activity?.indicativeOffers
    );
  }

  public get plansMaximumCapacity(): number {
    return +this.activity?.meta?.plansMaximumCapacity;
  }

  public get responsiveDividerVariant(): string {
    return this.isTabletDevice
      ? DividerVariantEnum.Large
      : DividerVariantEnum.Medium;
  }

  public submit(): void {
    this.redirectionService.redirect('/organizer.activity-planning.activity', {
      queryParams: { activityId: this.activity.sku },
    });
  }
}
